import { useEffect } from "react";
import { medicalWebPage } from "@/lib/schema";

/**
 * Lightweight document-head manager for this client-rendered SPA.
 *
 * The codebase has no react-helmet; pages set `document.title` by hand. This
 * hook extends that pattern to manage title, meta description, canonical,
 * rel=prev/next, Open Graph/Twitter tags and JSON-LD — and, crucially, cleans
 * everything up on unmount so a stale canonical/description never leaks onto the
 * next route.
 */
export const SITE_URL = "https://myvaidyam.com";

type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;

export interface DocumentHead {
  title: string;
  description?: string;
  canonical?: string;
  prev?: string | null;
  next?: string | null;
  ogImage?: string;
  ogType?: string;
  /** images the browser should fetch at once (the first screen's main picture);
   *  written into the page's HTML by the prerender, as preload links */
  preloadImages?: string[];
  robots?: string;
  /** Written to <meta name="keywords"> (comma-separated). */
  keywords?: string[];
  jsonLd?: JsonLd;
}

const MANAGED = "data-mv-head";

/**
 * Prerender capture.
 *
 * The site is client-rendered, so everything this hook injects is invisible to
 * any crawler or SEO checker that does not run JavaScript — they all see the
 * one static index.html instead. scripts/prerender-head.tsx renders each route
 * under Node and reads what the page asked for from here, then bakes it into
 * that route's HTML. Collecting it in the hook means the prerenderer uses each
 * page's real head rather than a second, drifting copy of the same logic.
 *
 * `document` is absent only under Node, so this costs the browser nothing.
 */
const SSR = typeof document === "undefined";
export const capturedHeads: DocumentHead[] = [];

/**
 * index.html's own Open Graph / Twitter tags, at their site-wide values. The
 * prerender writes the loaded page's values into these same tags (a second tag
 * after them would lose to the first one in a link preview), so they are put
 * back here when that page's head is dropped.
 */
const SHELL_TAGS: Array<["name" | "property", string, string]> = [
  ["property", "og:title", "MyVaidyam - Authentic Ayurvedic Centers & Healing Retreats"],
  ["property", "og:description", "20 years of medical tourism experience guiding you to India's best Ayurvedic centers for personalized care."],
  ["property", "og:type", "website"],
  ["property", "og:image", `${SITE_URL}/myvaidyam-logo.png`],
  ["name", "twitter:image", `${SITE_URL}/myvaidyam-logo.png`],
];

/**
 * Removes every tag scripts/prerender-head.tsx baked into the served HTML
 * (all marked data-mv-pre). They describe the URL that was loaded, so they must
 * go once React takes over the head, or once the visitor navigates elsewhere.
 * Done once: afterwards there is nothing prerendered left, and the tags belong
 * to whichever page set them.
 */
export function clearPrerenderedHead() {
  const baked = document.head.querySelectorAll('[data-mv-pre="1"]');
  if (!baked.length) return;
  baked.forEach((el) => el.remove());
  for (const [attr, key, content] of SHELL_TAGS) {
    document.head.querySelector(`meta[${attr}="${key}"]`)?.setAttribute("content", content);
  }
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    created = true;
  }
  const prevContent = el.getAttribute("content");
  el.setAttribute("content", content);
  if (created) {
    el.setAttribute(MANAGED, "1");
    document.head.appendChild(el);
    return () => el && el.parentNode && el.parentNode.removeChild(el);
  }
  // pre-existing tag (e.g. the static description from index.html): restore it
  return () => {
    if (el && prevContent !== null) el.setAttribute("content", prevContent);
  };
}

function createLink(rel: string, href: string) {
  const el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  el.setAttribute(MANAGED, "1");
  document.head.appendChild(el);
  return () => el.parentNode && el.parentNode.removeChild(el);
}

/**
 * Every indexable page carries a MedicalWebPage. Pages written before that was
 * the rule (the hubs, the patient stories) set only a BreadcrumbList or an
 * ItemList, so one is added here from the page's own title, description and
 * canonical — the same values it already publishes — rather than in each file.
 */
function withWebPage(head: DocumentHead): DocumentHead {
  if (!head.canonical || /noindex/i.test(head.robots ?? "")) return head;
  const nodes = head.jsonLd ? (Array.isArray(head.jsonLd) ? head.jsonLd : [head.jsonLd]) : [];
  if (nodes.some((n) => n && n["@type"] === "MedicalWebPage")) return head;
  const name = head.title.replace(/\s*[|–—]\s*MyVaidyam.*$/i, "").trim() || head.title;
  const path = head.canonical.replace(/^https?:\/\/[^/]+/, "") || "/";
  return { ...head, jsonLd: [medicalWebPage({ name, path, description: head.description }), ...nodes] };
}

// the brand logo (/myvaidyam-logo.png; the old /logo.png was removed 2026-11-02)
const isLogo = (url?: string) => !url || /\/(myvaidyam-)?logo\.(png|webp|jpe?g)$/i.test(url);

/**
 * The page's own photo on its MedicalWebPage (image and primaryImageOfPage),
 * the same picture it shares (og:image), never the logo (client, 2026-11-02).
 * Pages whose photo is picked later (the prerender, PageSeoScope) get it there.
 */
export function withPageImage(head: DocumentHead): DocumentHead {
  if (isLogo(head.ogImage) || !head.jsonLd) return head;
  const image = { "@type": "ImageObject", url: head.ogImage };
  const add = (n: Record<string, unknown>) =>
    n && n["@type"] === "MedicalWebPage" && !n.primaryImageOfPage ? { ...n, image, primaryImageOfPage: image } : n;
  return { ...head, jsonLd: Array.isArray(head.jsonLd) ? head.jsonLd.map(add) : add(head.jsonLd) };
}

export function useDocumentHead(input: DocumentHead) {
  const head = withPageImage(withWebPage(input));
  const {
    title,
    description,
    canonical,
    prev,
    next,
    ogImage,
    ogType = "website",
    robots,
    keywords,
    jsonLd,
  } = head;
  const keywordList = keywords?.length ? keywords.join(", ") : "";

  // Serialise deps so object identity changes don't cause redundant work.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  if (SSR) capturedHeads.push(head);

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // Discard the prerendered head tags now that React is driving the head.
    // scripts/prerender-head.tsx bakes these same tags into each route's HTML
    // for crawlers that do not run JavaScript; once hydrated we re-create them
    // below, and leaving both would give two canonicals and hand a validator two
    // copies of every @id — which it merges into one item with each property
    // listed twice.
    clearPrerenderedHead();

    if (title) document.title = title;

    if (description) {
      cleanups.push(upsertMeta("name", "description", description));
      cleanups.push(upsertMeta("property", "og:description", description));
      cleanups.push(upsertMeta("name", "twitter:description", description));
    }
    if (title) {
      cleanups.push(upsertMeta("property", "og:title", title));
      cleanups.push(upsertMeta("name", "twitter:title", title));
    }
    cleanups.push(upsertMeta("property", "og:type", ogType));
    if (canonical) {
      cleanups.push(createLink("canonical", canonical));
      cleanups.push(upsertMeta("property", "og:url", canonical));
    }
    if (ogImage) {
      cleanups.push(upsertMeta("property", "og:image", ogImage));
      cleanups.push(upsertMeta("name", "twitter:image", ogImage));
    }
    if (prev) cleanups.push(createLink("prev", prev));
    if (next) cleanups.push(createLink("next", next));
    if (robots) cleanups.push(upsertMeta("name", "robots", robots));
    if (keywordList) cleanups.push(upsertMeta("name", "keywords", keywordList));

    if (jsonLd) {
      // One <script> per entity, never a single array or @graph. Validators
      // resolve @id references by nesting the referenced node inside the one
      // that points at it, so a combined block makes MedicalClinic and
      // BreadcrumbList disappear into MedicalWebPage instead of being listed
      // as items of their own. Separate, unlinked blocks keep each type
      // visible (see the note in src/lib/schema.ts).
      const nodes = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const node of nodes) {
        if (!node) continue;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(MANAGED, "1");
        script.text = JSON.stringify(node);
        document.head.appendChild(script);
        cleanups.push(() => script.parentNode && script.parentNode.removeChild(script));
      }
    }

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, prev, next, ogImage, ogType, robots, keywordList, jsonLdKey]);
}
